"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Devshayani Ekadashi (91225dd9) phrase overlays — Trimbakeshwar holes treatment. Top-left,
// yellow-prominent, per-word colour + line breaks, black drop-shadow, scattered-holes mask,
// word-by-word drop-in. Times ABSOLUTE seconds on the assembled narration (jyotirlinga — no
// opener shift). 1280×704. Every `start` is the EXACT word_timestamps_hindi time of the phrase's
// key word (verified against WTS, 3585 words, ends 1315.05s — not drifted).
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 36 phrase cards. Numerals LATIN (21, 200, 700, 13वीं, 1974, 2014); names/terms Devanagari.
// out = start + ~3.4s. Close pairs merged into single 2-line cards (ईंट/वीट/विठ्ठल; 2014/सुप्रीम कोर्ट)
// so two top-left holes cards never collide.
export const DEVSHAYANI_PHRASES: Phrase[] = [
  // ── HOOK ──
  { start: 8.53,    out: 12.30,   lines: [[{ t: "इक्कीस", c: "y", big: true }, { t: "दिन", c: "w" }], [{ t: "200", c: "w", big: true }, { t: "KM", c: "y" }]] },
  { start: 25.77,   out: 29.17,   lines: [[{ t: "देवशयनी", c: "y", big: true }], [{ t: "एकादशी", c: "w" }]] },
  { start: 32.31,   out: 35.71,   lines: [[{ t: "चार", c: "y" }, { t: "महीने", c: "w" }], [{ t: "योगनिद्रा", c: "w", big: true }]] },
  { start: 58.57,   out: 62.00,   lines: [[{ t: "एक", small: true }, { t: "ईंट", c: "y", big: true }, { t: "पर", small: true }], [{ t: "जागता", c: "w", big: true }, { t: "रहता", small: true }, { t: "है", small: true }]] },
  { start: 115.60,  out: 119.00,  lines: [[{ t: "सुप्रीम", c: "y" }], [{ t: "कोर्ट", c: "w", big: true }, { t: "तक", small: true }]] },

  // ── EKADASHI KA JANM / CHATURMAS ──
  { start: 160.54,  out: 163.94,  lines: [[{ t: "मुर", c: "r", big: true }], [{ t: "असुर", c: "w" }]] },
  { start: 208.91,  out: 212.31,  lines: [[{ t: "ग्यारहवीं", small: true }, { t: "तिथि", small: true }], [{ t: "एकादशी", c: "y", big: true }]] },
  { start: 270.15,  out: 273.55,  lines: [[{ t: "चातुर्मास", c: "y", big: true }]] },
  { start: 296.99,  out: 300.39,  lines: [[{ t: "चावल", c: "w", big: true }, { t: "मत", small: true }], [{ t: "खाओ", c: "r" }]] },

  // ── COSMIC CLOCK ──
  { start: 336.91,  out: 340.31,  lines: [[{ t: "उत्तरायण", c: "y", big: true }]] },
  { start: 344.16,  out: 347.56,  lines: [[{ t: "दक्षिणायन", c: "w", big: true }]] },

  // ── PANDHARPUR / PUNDALIK ──
  { start: 422.78,  out: 426.18,  lines: [[{ t: "पंढरपुर", c: "y", big: true }]] },
  { start: 440.75,  out: 444.15,  lines: [[{ t: "पुंडलिक", c: "w", big: true }]] },
  { start: 531.27,  out: 539.00,  lines: [[{ t: "मराठी", small: true }, { t: "में", small: true }, { t: "वीट", c: "y" }], [{ t: "विठ्ठल", c: "w", big: true }]] },
  { start: 544.23,  out: 547.63,  lines: [[{ t: "पांडुरंग", c: "y" }], [{ t: "माउली", c: "w", big: true }]] },

  // ── PROOF IN STONE ──
  { start: 576.15,  out: 579.55,  lines: [[{ t: "13वीं", c: "y" }, { t: "सदी", small: true }], [{ t: "शिलालेख", c: "w", big: true }]] },
  { start: 587.69,  out: 591.09,  lines: [[{ t: "700", c: "w", big: true }, { t: "साल", c: "y" }]] },
  { start: 602.48,  out: 605.88,  lines: [[{ t: "रामचंद्र", small: true }], [{ t: "ढेरे", c: "y", big: true }, { t: "की", small: true }, { t: "शोध", small: true }]] },

  // ── VARI / DAILY ROUTINE ──
  { start: 648.41,  out: 651.81,  lines: [[{ t: "पैदल", small: true }, { t: "यात्रा", small: true }], [{ t: "वारी", c: "y", big: true }]] },
  { start: 679.20,  out: 682.60,  lines: [[{ t: "रिंगण", c: "y", big: true }]] },
  { start: 733.65,  out: 737.05,  lines: [[{ t: "सुबह", small: true }, { t: "4", c: "w", big: true }, { t: "बजे", small: true }], [{ t: "जगाते", c: "y" }, { t: "हैं", small: true }]] },
  { start: 748.96,  out: 752.36,  lines: [[{ t: "20", c: "w", big: true }, { t: "घंटे", c: "y" }], [{ t: "लंबा", small: true }, { t: "दिन", small: true }]] },

  // ── WEALTH / COURT ──
  { start: 778.72,  out: 782.12,  lines: [[{ t: "धन", c: "r", big: true }], [{ t: "सत्ता", small: true }, { t: "की", small: true }, { t: "जंग", c: "r" }]] },
  { start: 814.75,  out: 818.15,  lines: [[{ t: "मंदिर", small: true }, { t: "अधिनियम", c: "y" }], [{ t: "1974", c: "w", big: true }]] },
  { start: 841.29,  out: 845.50,  lines: [[{ t: "2014", c: "w" }], [{ t: "सुप्रीम", c: "y" }, { t: "कोर्ट", c: "w", big: true }]] },

  // ── VAMAN / BALI ──
  { start: 1024.17, out: 1027.57, lines: [[{ t: "राजा", small: true }], [{ t: "बलि", c: "y", big: true }]] },
  { start: 1044.17, out: 1047.57, lines: [[{ t: "दान", small: true }, { t: "का", small: true }], [{ t: "घमंड", c: "r", big: true }]] },
  { start: 1057.61, out: 1061.01, lines: [[{ t: "वामन", c: "y", big: true }], [{ t: "अवतार", c: "w" }]] },
  { start: 1066.71, out: 1070.11, lines: [[{ t: "तीन", c: "y" }, { t: "पग", c: "w", big: true }], [{ t: "ज़मीन", small: true }]] },
  { start: 1113.35, out: 1116.75, lines: [[{ t: "पाताल", c: "r", big: true }]] },
  { start: 1151.99, out: 1155.39, lines: [[{ t: "भगवान", small: true }, { t: "बने", small: true }], [{ t: "द्वारपाल", c: "w", big: true }]] },

  // ── CLOSE ──
  { start: 1199.38, out: 1202.78, lines: [[{ t: "एक", small: true }, { t: "सच", small: true }, { t: "के", small: true }], [{ t: "तीन", c: "y" }, { t: "चेहरे", c: "w", big: true }]] },
  { start: 1209.46, out: 1212.86, lines: [[{ t: "वचन", c: "w" }, { t: "का", small: true }], [{ t: "उत्सव", c: "y", big: true }]] },
  { start: 1290.37, out: 1293.77, lines: [[{ t: "श्रीकृष्ण", c: "y" }, { t: "की", small: true }], [{ t: "मृत्यु", c: "r", big: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-RIGHT viewing tip — two lines, yellow "Watch at 1.2x" + smaller white
// "for best experience". NO holes mask. 0→5s only.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, fontFamily, opacity: op,
                  display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x
      </span>
      <span style={{ color: WHITE, fontSize: Math.round(SMALL_FS * 0.7), fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        for best experience
      </span>
    </div>
  );
};

// Bottom-LEFT "आगे देखिए" teaser — SMALL_FS, yellow text, WHITE cycling dots. NO holes mask.
// Runs over the opening hook, 4→112s (auto-tiled by build_overlay_windows.py since >40s).
const AageDekhiye: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.5, out - 0.6, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const n = Math.floor((t * 1.5) % 4); // 0,1,2,3 dots
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        आगे देखिए<span style={{ display: "inline-block", width: "1.4em", textAlign: "left", color: WHITE }}>{".".repeat(n)}</span>
      </span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (teaser, watch tip, LS pills).
export const DevshayaniOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = DEVSHAYANI_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* आगे देखिए teaser (bottom-left, cycling dots) over the opening hook */}
      <AageDekhiye fps={fps} start={4.0} out={112.0} />

      {/* viewing tip (bottom-right) */}
      <WatchTag fps={fps} start={0.0} out={5.0} />

      {/* Like+Subscribe — ALL THREE subscribe beats covered (do not miss any):
          LS2 (bell) @ "सब्सक्राइब" 143.28s · LS2 (bell) @ mid "सब्सक्राइब" 1003.16s ·
          LS5 (no bell) @ outro "सब्सक्राइब" 1302.83s */}
      <Sequence from={S(143.28)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1003.16)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1302.83)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
